// src/components/pages/admin-dash/GlobalConfigurationAdmin.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Spinner from "../../ui/Spinner";

/**
 * Admin view over the single `globalconfiguration` row
 * (GlobalConfigurationAdminResource, /admin/global-configuration) —
 * previously only editable by hand in the DB (1-ADMIN_ROADMAP.md item 2.4).
 *
 * `deployDate` is read-only here on purpose — it's set once when the
 * row is seeded, not a day-to-day admin knob.
 */
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function GlobalConfigurationAdmin({ onShowToast }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [noReplyEmail, setNoReplyEmail] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [webAddress, setWebAddress] = useState("");
  const [alertRecipientEmails, setAlertRecipientEmails] = useState("");
  const [weeklyReportEnabled, setWeeklyReportEnabled] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/admin/global-configuration");
      setConfig(res.data);
      setNoReplyEmail(res.data?.noReplyEmail || "");
      setFromEmail(res.data?.fromEmail || "");
      setFromName(res.data?.fromName || "");
      setWebAddress(res.data?.webAddress || "");
      setAlertRecipientEmails(res.data?.alertRecipientEmails || "");
      setWeeklyReportEnabled(!!res.data?.weeklyReportEnabled);
    } catch {
      setError("Couldn't load global configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const dirty = config && (
    noReplyEmail !== (config.noReplyEmail || "") ||
    fromEmail !== (config.fromEmail || "") ||
    fromName !== (config.fromName || "") ||
    webAddress !== (config.webAddress || "") ||
    alertRecipientEmails !== (config.alertRecipientEmails || "") ||
    weeklyReportEnabled !== !!config.weeklyReportEnabled
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axiosInstance.put("/admin/global-configuration", {
        noReplyEmail, fromEmail, fromName, webAddress, alertRecipientEmails, weeklyReportEnabled,
        scratchEnabled: config.scratchEnabled,
      });
      setConfig(res.data);
      onShowToast?.("Global configuration updated.", "success");
    } catch (err) {
      onShowToast?.(err?.response?.data?.message || "Failed to save configuration.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Global configuration
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Site-wide contact/from addresses and web address. Takes effect immediately — no restart
          needed.
        </p>
      </header>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size={18} />
          </div>
        ) : config ? (
          <form onSubmit={handleSave} className="space-y-1">
            <div className="mb-3.5">
              <div className="lg-label mb-1">Deploy date</div>
              <div className="text-[13.5px]" style={{ color: "var(--txt)" }}>{fmtDate(config.deployDate)}</div>
            </div>

            <Field
              label="From name"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="e.g. Lotto GPT"
            />
            <Field
              label="From email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="e.g. no-reply@mail.lotto-gpt.io"
            />
            <Field
              label="No-reply email"
              value={noReplyEmail}
              onChange={(e) => setNoReplyEmail(e.target.value)}
              placeholder="e.g. no-reply@mail.lotto-gpt.io"
            />
            <Field
              label="Web address"
              value={webAddress}
              onChange={(e) => setWebAddress(e.target.value)}
              placeholder="e.g. https://lotto-gpt.io"
            />
            <Field
              label="Alert recipient emails"
              value={alertRecipientEmails}
              onChange={(e) => setAlertRecipientEmails(e.target.value)}
              placeholder="e.g. ops@lotto-gpt.io, admin@lotto-gpt.io (comma-separated)"
              hint="Who gets notified when a scheduled job or scratch-off ingestion source fails. Leave blank to disable alerts."
            />

            <label className="flex items-center gap-2 text-[13px] mb-3.5 mt-1" style={{ color: "var(--txt)" }}>
              <input
                type="checkbox"
                checked={weeklyReportEnabled}
                onChange={(e) => setWeeklyReportEnabled(e.target.checked)}
              />
              Send weekly financial digest to the alert recipients above (every Monday)
            </label>

            <div className="flex items-center justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <Button icon="check" size="sm" type="submit" loading={saving} disabled={!dirty}>
                Save changes
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-[13px] py-4 text-center" style={{ color: "var(--txt-2)" }}>
            No configuration row found.
          </p>
        )}
      </Card>
    </div>
  );
}
